import { HelpCenterKnowledge } from '@sgwt/sgwt-widgets-react';
import { useCallback, useMemo } from 'react';
import { tutorialPdfDownloadTagName } from 'webcomponents/tutorial-pdf-download';
import { tutorialVideoTagName } from 'webcomponents/tutorial-video';

import { useHelpCenter } from './use-help-center';

export interface ProductInfo {
  hasVideo: boolean;
  hasFactSheet: boolean;
}

function extractProducts({ topics }: HelpCenterKnowledge): Record<string, ProductInfo | undefined> | undefined {
  return Object.fromEntries(
    topics
      ?.filter(({ categoryId }) => categoryId.startsWith('product.'))
      .map(
        ({ id, content }) =>
          [
            id,
            {
              hasVideo: content?.includes(tutorialVideoTagName) ?? false,
              hasFactSheet: content?.includes(tutorialPdfDownloadTagName) ?? false,
            },
          ] as const,
      ) ?? [],
  );
}

export function useHelpCenterProducts() {
  const { knowledge, isHelpCenterReadyAndExpanded, openTopicId, openCategoryId } = useHelpCenter();

  const availableProducts = useMemo(() => (knowledge ? extractProducts(knowledge) : undefined), [knowledge]);

  const openLearnMore = useCallback(
    (productType?: string, productSubType?: string, autoDownloadPdf = false) => {
      if (!isHelpCenterReadyAndExpanded()) {
        return;
      }

      openTopicId(`${productType}.${productSubType}`);

      if (autoDownloadPdf) {
        setTimeout(() => {
          document.querySelector<HTMLAnchorElement>('tutorial-pdf-download a')?.click();
        });
      }
    },
    [isHelpCenterReadyAndExpanded, openTopicId],
  );

  const openProductCatalogue = useCallback(() => {
    if (!isHelpCenterReadyAndExpanded()) {
      return;
    }
    openCategoryId('product.catalogue');
  }, [isHelpCenterReadyAndExpanded, openCategoryId]);

  const getProduct = useCallback(
    (productType?: string, productSubType?: string) => availableProducts?.[`${productType}.${productSubType}`],
    [availableProducts],
  );

  return { availableProducts, openLearnMore, openProductCatalogue, getProduct };
}
